import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Star, Warning, ArrowClockwise, Gear, PaperPlaneTilt, CheckCircle, ChatCircleText } from "@phosphor-icons/react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * Admin panel — Avis Clients.
 *
 * - Lists all rated interventions (newest first)
 * - Global stats: average, count, breakdown by star
 * - Filter by minimum rating + since date
 * - Auto-send settings toggle
 */
export default function ClientReviewsPanel() {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [minRating, setMinRating] = useState(1);
  const [since, setSince] = useState("");
  const [settings, setSettings] = useState(null);

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const params = {};
      if (minRating > 1) params.min_rating = minRating;
      if (since) params.since = since;
      const [reviewsRes, settingsRes] = await Promise.all([
        api.get("/admin/reviews", { params }),
        api.get("/admin/intervention-rating-settings"),
      ]);
      setState({ loading: false, data: reviewsRes.data, error: null });
      setSettings(settingsRes.data);
    } catch (err) {
      setState({ loading: false, data: null, error: err.response?.data?.detail || "Erreur de chargement" });
    }
  };

  useEffect(() => { load(); }, [minRating, since]);  // load re-runs on filter changes

  const saveSettings = async (patch) => {
    try {
      const r = await api.patch("/admin/intervention-rating-settings", { ...settings, ...patch });
      setSettings(r.data);
      toast.success("Réglages enregistrés");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  if (state.loading && !state.data) {
    return <div className="text-white/50 text-sm py-10 text-center">Chargement des avis…</div>;
  }
  if (state.error) {
    return (
      <div className="rounded-2xl bg-red-500/10 border border-red-500/30 p-4 flex items-center gap-3 text-red-300">
        <Warning size={20} weight="duotone" /> <span className="text-sm">{state.error}</span>
      </div>
    );
  }

  const d = state.data || { total: 0, average: null, by_star: {}, rows: [] };
  return (
    <div className="space-y-4" data-testid="reviews-panel">
      {/* Header : KPI globaux */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <StatCard
          label="Note moyenne"
          value={d.average != null ? `${d.average.toFixed(2)} / 5` : "—"}
          sub={`${d.total} avis reçus`}
          accent="#F59E0B"
          testid="reviews-stat-avg"
        />
        <StatCard
          label="Répartition"
          value={<Distribution byStar={d.by_star} total={d.total} />}
          sub="Nombre par note"
          accent="#005CFF"
        />
        <StatCard
          label="Auto-envoi"
          value={settings?.enabled && settings?.auto_send_on_done ? "Activé" : "Désactivé"}
          sub={settings?.send_channel === "whatsapp_sms" ? "WhatsApp + fallback SMS" : settings?.send_channel}
          accent={settings?.enabled ? "#10B981" : "#94A3B8"}
        />
      </div>

      {/* Réglages */}
      {settings && (
        <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-3" data-testid="reviews-settings">
          <div className="flex items-center gap-2 font-display font-semibold text-sm">
            <Gear size={16} weight="duotone" className="text-[#7CB2FF]" /> Envoi automatique à la clôture
          </div>
          <div className="grid md:grid-cols-2 gap-3 text-sm">
            <Row label="Système activé">
              <Switch checked={!!settings.enabled} onCheckedChange={(v) => saveSettings({ enabled: v })} data-testid="reviews-toggle-enabled" />
            </Row>
            <Row label="Auto-envoi WhatsApp à la clôture">
              <Switch checked={!!settings.auto_send_on_done} onCheckedChange={(v) => saveSettings({ auto_send_on_done: v })} data-testid="reviews-toggle-auto" />
            </Row>
            <Row label="Canal">
              <select
                value={settings.send_channel || "whatsapp_sms"}
                onChange={(e) => saveSettings({ send_channel: e.target.value })}
                className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-xs"
                data-testid="reviews-select-channel"
              >
                <option value="whatsapp_sms">WhatsApp + fallback SMS</option>
                <option value="whatsapp">WhatsApp uniquement</option>
                <option value="sms">SMS uniquement</option>
                <option value="none">Aucun (manuel)</option>
              </select>
            </Row>
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-white/50">Filtrer :</span>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            onClick={() => setMinRating(n)}
            data-testid={`reviews-filter-${n}`}
            className={`px-3 py-1.5 rounded-full border ${minRating === n ? "bg-amber-500/20 border-amber-500/60 text-amber-200" : "bg-white/[0.03] border-white/10 text-white/60"}`}
          >
            ≥ {n}★
          </button>
        ))}
        <input
          type="date"
          value={since}
          onChange={(e) => setSince(e.target.value)}
          className="bg-white/5 border border-white/10 rounded-full px-3 py-1.5 text-xs"
          data-testid="reviews-filter-since"
        />
        {(minRating > 1 || since) && (
          <button
            onClick={() => { setMinRating(1); setSince(""); }}
            className="text-white/50 hover:text-white"
          >Réinitialiser</button>
        )}
        <button
          onClick={load}
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-white/70"
        ><ArrowClockwise size={12} weight="bold" /> Rafraîchir</button>
      </div>

      {/* Liste */}
      <div className="space-y-2" data-testid="reviews-list">
        {(d.rows || []).length === 0 ? (
          <div className="rounded-2xl bg-white/[0.02] border border-dashed border-white/10 p-8 text-center text-white/40 text-sm">
            <ChatCircleText size={28} weight="duotone" className="mx-auto text-white/25 mb-2" />
            Aucun avis pour ces filtres. Une fois une intervention clôturée, le client reçoit automatiquement un lien pour vous noter.
          </div>
        ) : (
          d.rows.map((r) => <ReviewRow key={r.id} row={r} />)
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, accent, testid }) {
  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">{label}</div>
      <div className="font-display font-bold text-2xl" style={{ color: accent }}>{value}</div>
      {sub && <div className="text-[11px] text-white/50 mt-0.5">{sub}</div>}
    </div>
  );
}

function Distribution({ byStar, total }) {
  const stars = [5, 4, 3, 2, 1];
  return (
    <div className="space-y-1 pt-1">
      {stars.map((n) => {
        const count = byStar?.[n] || 0;
        const pct = total ? Math.round((count / total) * 100) : 0;
        return (
          <div key={n} className="flex items-center gap-2 text-[11px]">
            <span className="w-4 text-amber-300 font-mono">{n}</span>
            <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-amber-500 to-amber-300" style={{ width: `${pct}%` }} />
            </div>
            <span className="w-6 text-right text-white/50 font-mono">{count}</span>
          </div>
        );
      })}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-3 bg-white/[0.02] border border-white/5 rounded-lg px-3 py-2">
      <span className="text-white/70">{label}</span>
      {children}
    </div>
  );
}

function ReviewRow({ row }) {
  const stars = row.rating || 0;
  const positive = stars >= 4;
  const critical = stars <= 2;
  const border = critical ? "border-red-500/30" : positive ? "border-amber-500/25" : "border-white/10";
  const bg = critical ? "bg-red-500/[0.05]" : positive ? "bg-amber-500/[0.05]" : "bg-white/[0.03]";
  const ratedAt = row.rated_at ? new Date(row.rated_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : "";
  return (
    <div className={`rounded-2xl ${bg} border ${border} p-3 space-y-2`} data-testid={`review-row-${row.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star key={n} size={14} weight={n <= stars ? "fill" : "regular"} className={n <= stars ? "text-amber-400" : "text-white/20"} />
            ))}
            <span className="ml-1.5 text-xs font-mono text-white/70">{stars}/5</span>
          </div>
          <div className="mt-1 font-display font-semibold text-sm truncate">{row.chantier_name || row.title || "Intervention"}</div>
          <div className="text-[11px] text-white/50 truncate">
            {row.client_name || "Client"}{row.technician_name ? ` · ${row.technician_name}` : ""}
          </div>
        </div>
        <div className="text-[10px] text-white/40 shrink-0 text-right">{ratedAt}</div>
      </div>
      {row.rating_comment && (
        <div className="text-sm text-white/80 italic border-l-2 border-white/10 pl-3 py-0.5">« {row.rating_comment} »</div>
      )}
      {critical && (
        <div className="text-[11px] text-red-300 flex items-center gap-1.5"><Warning size={11} weight="bold" /> À rappeler</div>
      )}
      {row.rating_sent_at && !row.rated_at && (
        <div className="text-[10px] text-white/40 flex items-center gap-1"><PaperPlaneTilt size={10} /> Lien envoyé</div>
      )}
    </div>
  );
}
